import axios from "axios";
import cryptoRandomString from "crypto-random-string";


import {
  AUTH_FAILURE,
  OTHER_FAILURE,
  EXPIRED_FAILURE,
  OAuth2SessionError,
} from "./OAuth2SessionError";
import { JwtToken } from "./JwtToken";
import { VuexTokenStore } from "./VuexTokenStore";

// Calculate the SHA256 hash of the input text.
// Returns a promise that resolves to an ArrayBuffer
function sha256(plain) {
  const encoder = new TextEncoder();
  const data = encoder.encode(plain);
  return window.crypto.subtle.digest("SHA-256", data);
}

// Base64-urlencodes the input string
function base64urlencode(str) {
  // Convert the ArrayBuffer to string using Uint8 array to conver to what btoa accepts.
  // btoa accepts chars only within ascii 0-255 and base64 encodes them.
  // Then convert the base64 encoded to base64url encoded
  //   (replace + with -, replace / with _, trim trailing =)
  return window.btoa(String.fromCharCode.apply(null, new Uint8Array(str)))
    .replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

// Return the base64-urlencoded sha256 hash for the PKCE challenge
async function pkceChallengeFromVerifier(v) {
  const hashed = await sha256(v);
  return base64urlencode(hashed);
}

export class OAuth2Service{
  constructor(options = {}) {
    const {
      clientId = "web",
      clientSecret = "web",
      grantURL = "/oauth2/token",
      code = {},
    } = options;

    this.clientId = clientId;
    this.clientSecret = clientSecret;
    this.grantURL = grantURL;

    //redirect URI correspond à l'URL de retour sur l'application
    //
    const {
      // URL de retour dans l'application SPA
      redirectUri = `${window.location.origin}/oauth_callback`,
      redirectMethod = uri => window.location.replace(uri),
      //  URL de login sur le serveur Oauth2 (servira dans la pop-up)
      authorizeUri = "/oauth2/authorize",
      //URL de logout sur le serveur Oauth2
      logoutUri = "/logout",
      storage = sessionStorage,
      stateKey = "OAuth2Service.state",
      codeVerifierKey = "OAuth2Service.codeVerifier",
    } = code;

    this.redirectUri = redirectUri;
    this.redirectMethod = redirectMethod;
    this.authorizeUri = authorizeUri;
    this.logoutUri = logoutUri;
    this.storage = storage;
    this.stateKey = stateKey;
    this.codeVerifierKey = codeVerifierKey;
  }

  /**
   * Création d'un plugin vuex
   * @param {*} storename
   * @returns
   */
  createVuexPlugin(storename) {
    return store => {
      this.tokenStore = new VuexTokenStore(store, storename);
    }
  }

  /**
   * Renvoi vrai si l'utilisateur est loggé
   * @returns
   */
  logged() {
    return !!this._tokenStore.getAccessToken();
  }

  /**
  * Renvoi vrai si le token est expiré
  * @returns
  */
  expired() {
    return !this._tokenStore.getRefreshToken();
  }

  /**
   * Déconnexion
   * @returns
   */
  logout() {
    //attention doit réaliser le single logout

    this._tokenStore.logout();
    axios.post(this.logoutUri);
  }

  /**
   * Renvoi les droits de l'access token si présent
   * @returns
   */
  authorities() {
    return this._tokenStore.getAccessToken()?.authorities || [];
  }

  /**
   * Place un token store qui définit les fonctions
   * function useToken(accessToken:JwtToken, refreshToken:JwtToken): void
   * function getRefreshToken(): JwtToken
   * function getAccessToken(): JwtToken
   *
   */
  set tokenStore(tokenStore) {
    this._tokenStore = tokenStore;
  }

  _executeGrantRequest(data) {
    data.append("client_id", this.clientId);
    data.append("client_secret", this.clientSecret);
    return axios
      .post(this.grantURL, data)
      .then(reponse => {
        return this._useOAuth2Token(reponse.data);
      })
      .catch(err => {
        const status = err.response?.status;
        if (status === 401) {
          throw new OAuth2SessionError(AUTH_FAILURE, err);
        } else {
          throw new OAuth2SessionError(OTHER_FAILURE, err);
        }
      });
  }

  _useOAuth2Token(oauth2token) {
    const accessToken = new JwtToken(oauth2token.access_token);
    const refreshToken = oauth2token.refresh_token;
    this._tokenStore.useToken(accessToken, refreshToken);
    return this._extractTokenValue(accessToken);
  }

  _extractTokenValue(accessToken) {
    return accessToken.value;
  }

  /**
   * Démarre le login code
   */
  async loginCode() {
    const state = cryptoRandomString({ length: 64, type: "alphanumeric" });
    const codeVerifier = cryptoRandomString({ length: 128, type: "alphanumeric" });

    const challenge = await pkceChallengeFromVerifier(codeVerifier);

    const params = new URLSearchParams();
    params.append("client_id", this.clientId);
    params.append("response_type", "code");
    params.append("redirect_uri", this.redirectUri);
    params.append("state", state);
    params.append("code_challenge", challenge);
    params.append("code_challenge_method", "S256");

    this.storage.setItem(this.stateKey, state);
    this.storage.setItem(this.codeVerifierKey, codeVerifier);
    const uri = `${this.authorizeUri}?${params.toString()}`;
    this.redirectMethod(uri);
  }

  /**
   * Interception de l'URL de callback
   * @param {*} url
   * @returns
   */
  processCallbackUrl(url) {
    const group = url.match(/.*\?(.+)/);
    if (group) {
      const params = new URLSearchParams(group[1]);
      const state = params.get("state");
      const code = params.get("code");
      return this.processCode(code, state);
    } else {
      return Promise.reject("Erreur dans l'URL de retour");
    }
  }

  /**
  * Interception de l'URL de verification
  * @param {*} url
  * @returns
  */
  processVerificationUrl(url) {
    const group = url.match(/.*\?(.+)/);
    if (group) {
      const params = new URLSearchParams(group[1]);
      const code = params.get("code");
      return this.verificationCode(code);
    } else {
      return Promise.reject("Erreur dans l'URL de retour");
    }
  }

  /**
   * Permet de procéder à
   * @param {*} code
   * @param {*} state
   * @returns
   */
  processCode(code, state) {
    const savedState = this.storage.getItem(this.stateKey);
    this.storage.removeItem(this.stateKey);

    if (savedState !== state) {
      return Promise.reject(
        new Error(
          `paramètre state invalide state=${state}!==savedState=${savedState}`
        )
      );
    }

    const codeVerifier = this.storage.getItem(this.codeVerifierKey);
    this.storage.removeItem(this.codeVerifierKey);

    const params = new URLSearchParams();
    params.append("grant_type", "authorization_code");
    params.append("code", code);
    params.append("code_verifier", codeVerifier);
    params.append("redirect_uri", this.redirectUri);
    return this._executeGrantRequest(params);
  }

  verificationCode(code) {
    const params = new URLSearchParams();
    params.append("grant_type", "verification_code");
    params.append("code", code);
    return this._executeGrantRequest(params);
  }

  grantRefreshToken(refresh) {
    refresh = refresh || this._tokenStore.getRefreshToken();
    // force un refresh
    const params = new URLSearchParams();
    params.append("grant_type", "refresh_token");
    params.append("refresh_token", refresh);
    return this._executeGrantRequest(params);
  }

  /**
   * Renvoi une Promise après laquel un access token est disponible. La promise contient la valeur de l'access token utilisable en tant que Bearer token
   */
  withAccessToken() {
    const refresh = this._tokenStore.getRefreshToken();
    if (refresh) {
      const access = this._tokenStore.getAccessToken();
      if (!access.checkExpired()) {
        return Promise.resolve(this._extractTokenValue(access));
      } else {
        // force un refresh
       return  this.grantRefreshToken(refresh);
      }
    }

    return Promise.reject(new OAuth2SessionError(EXPIRED_FAILURE));
  }
}
